import {
  Button,
  Checkbox,
  Flex,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { modals } from '@mantine/modals'
import withErrorBoundaryAndPolicyCheck from '@v2/commons/HOC/ErrorBoundaryAndPolicyCheckHOC'
import ModalSelect from '@v2/commons/components/ModalSelect/modalSelect.component'
import { POLICIES } from '@v2/commons/config'
import useGetUsersNoPaginattionUseCase from '@v2/domains/user/hooks/useGetUsersNoPagination.useCase'
import { useEffect, useState } from 'react'

import { Icons } from 'src/common/components/ui'

import useAssignTask from '../../hooks/assignTask.hook'
import {
  TASK_ACTION_OPTIONS,
  TASK_PRIORITY_OPTIONS,
  TASK_STATUS_OPTIONS,
  TASK_SUBJECT_OPTIONS,
  TASK_TYPE_OPTIONS,
  TaskPriorityEnum,
  TaskStatusEnum,
  TaskTypeEnum,
} from '../../task.types'
import styles from './task-component.module.scss'

export interface ModalProps {
  selectedItems: string[]
  clearStore: () => void
  openSuccessAssignTaskModal: () => void
}

export const TOOLTIP_SUBJECT =
  'Please choose an action first to see related subjects'

const AssignTaskModal = ({
  selectedItems,
  clearStore,
  openSuccessAssignTaskModal,
}: ModalProps) => {
  const [taskSubjectValues, setTaskSubjectValues] = useState<
    typeof TASK_SUBJECT_OPTIONS
  >([])
  const { onSubmitForm, taskMutationStatus, user, assignTaskForm } =
    useAssignTask()

  const { assignees } = useGetUsersNoPaginattionUseCase()

  const [subject, setSubject] = useState<string | null | undefined>()
  const [action, setAction] = useState<string | null | undefined>(null)
  const [assignPerson, setAssignPerson] = useState<boolean | null | undefined>(
    assignTaskForm.getValues()['type'] === TaskTypeEnum.FOLLOW_UP,
  )

  assignTaskForm.watch('action', ({ value }) => {
    onSetSubjectValuesByAction(value)
    setAction(value)
  })

  assignTaskForm.watch('subject', ({ value }) => {
    setSubject(value)
  })

  assignTaskForm.watch('type', ({ value }) => {
    if (value === TaskTypeEnum.FOLLOW_UP) {
      setAssignPerson(true)
    } else {
      setAssignPerson(false)
      assignTaskForm.setFieldValue('isPersonAssign', false)
    }
  })

  const onSetSubjectValuesByAction = (value: any) => {
    const subjectOptions = TASK_SUBJECT_OPTIONS.filter((option) =>
      option.action.includes(value!),
    )
    setTaskSubjectValues(subjectOptions)
    setSubject(null)
  }

  useEffect(() => {
    if (taskMutationStatus.isSuccess) {
      clearStore()
      modals.closeAll()
      openSuccessAssignTaskModal()
    }
  }, [taskMutationStatus.isSuccess])

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault()
    onSubmitForm(selectedItems)
  }

  return (
    <div className={styles['modal-body']}>
      <Text className={styles['modal-header']} ta="center">
        Assign New Task
      </Text>
      <form
        onSubmit={(e) => {
          handleSubmit(e)
        }}
      >
        <SimpleGrid
          cols={{ base: 1, sm: 2 }}
          spacing="xl"
          className={styles['simpleGrid-root']}
        >
          <ModalSelect
            label="Type of Task"
            disabled
            options={TASK_TYPE_OPTIONS}
            showArrowIcon={false}
            {...assignTaskForm.getInputProps('type')}
            key={assignTaskForm.key('type')}
          />
          <ModalSelect
            label="Action"
            placeholder="Enter Action"
            {...assignTaskForm.getInputProps('action')}
            options={TASK_ACTION_OPTIONS}
            key={assignTaskForm.key('action')}
          />
          <Tooltip
            position="bottom"
            display={action ? 'none' : 'block'}
            label={TOOLTIP_SUBJECT}
          >
            <Paper>
              <ModalSelect
                label="Subject"
                placeholder="Enter Subject"
                {...assignTaskForm.getInputProps('subject')}
                options={taskSubjectValues}
                key={assignTaskForm.key('subject')}
                value={subject}
                showArrowIcon={!!action}
              />
            </Paper>
          </Tooltip>
          <ModalSelect
            label="Assign To"
            placeholder="Select Assignee"
            {...assignTaskForm.getInputProps('assignedToId')}
            options={assignees ?? []}
            key={assignTaskForm.key('assignedToId')}
            searchable
            nothingFoundMessage="No users found"
          />
          <DatePickerInput
            valueFormat="MM/DD/YYYY"
            placeholder="MM/DD/YYYY"
            label="Due Date"
            key={assignTaskForm.key('activityDate')}
            required
            rightSection={
              <Icons.Calendar className={styles['calender-icon']} />
            }
            highlightToday={true}
            {...assignTaskForm.getInputProps('activityDate')}
            popoverProps={{
              offset: 0,
              width: 'target',
              withinPortal: false,
              transitionProps: { transition: 'scale-y', duration: 300 },
              middlewares: {
                flip: false,
                shift: false,
                inline: true,
              },
            }}
            classNames={{
              input: styles['datePicker-input'],
              label: styles['datePicker-label'],
            }}
          />
          <ModalSelect
            required={false}
            label="Priority"
            placeholder="Select Priority"
            {...assignTaskForm.getInputProps('priority')}
            key={assignTaskForm.key('priority')}
            options={TASK_PRIORITY_OPTIONS}
            defaultValue={TaskPriorityEnum.MEDIUM}
          />
          <ModalSelect
            {...assignTaskForm.getInputProps('status')}
            defaultValue={TaskStatusEnum.OPEN}
            key={assignTaskForm.key('status')}
            label="Status"
            options={TASK_STATUS_OPTIONS}
            required={false}
          />
          <Stack style={{ alignSelf: 'center' }}>
            {assignPerson && (
              <Checkbox
                label="Assign Record to Organizer"
                radius="3px"
                {...assignTaskForm.getInputProps('isPersonAssign')}
                key={assignTaskForm.key('isPersonAssign')}
                mt={7}
              />
            )}
            <Checkbox
              label="Notify Assignee via Email"
              radius="3px"
              {...assignTaskForm.getInputProps('isNotify')}
              key={assignTaskForm.key('isNotify')}
              mt={7}
            />
          </Stack>
          <TextInput
            readOnly
            value={user.email}
            label="Submitted by"
            classNames={{
              input: styles['record-info-input'],
              label: styles['record-info-label'],
            }}
          />
          <DatePickerInput
            value={new Date()}
            valueFormat="MM/DD/YYYY"
            placeholder="MM/DD/YYYY"
            readOnly
            label="Submitted Date"
            rightSection={
              <Icons.Calendar className={styles['calender-icon']} />
            }
            highlightToday={true}
            classNames={{
              input: styles['datePicker-input'],
              label: styles['datePicker-label'],
            }}
          />
          <Textarea
            classNames={{
              root: styles['textArea-root'],
              label: styles['textArea-label'],
              input: styles['textArea-input'],
            }}
            {...assignTaskForm.getInputProps('note')}
            key={assignTaskForm.key('note')}
            label="Notes"
            withAsterisk
            placeholder="Enter your notes here..."
          />
        </SimpleGrid>
        <Flex justify="space-between" mt="30px">
          <Button
            variant="default"
            className={styles['button-cancel']}
            disabled={taskMutationStatus.isLoading}
            onClick={() => {
              modals.closeAll()
            }}
          >
            Cancel
          </Button>
          <Button
            rightSection={<Icons.ArrowRight />}
            className={styles['button-save']}
            type="submit"
            disabled={taskMutationStatus.isLoading}
            loading={taskMutationStatus.isLoading}
          >
            Save
          </Button>
        </Flex>
      </form>
    </div>
  )
}

export default withErrorBoundaryAndPolicyCheck(
  AssignTaskModal,
  POLICIES.isCreateTaskEnabled,
)
